/**
 * 채팅 데이터 Prefetching
 * - Link hover시 미리 데이터 로드
 * - Request deduplication으로 중복 방지
 * - 체감 속도 대폭 개선
 */

import { chatCache } from './chat-cache'

// 진행 중인 요청 추적 (request deduplication)
const pendingRequests = new Map<string, Promise<any>>()

/**
 * 채팅방 리스트 prefetch
 *
 * @param authToken JWT 토큰
 * @param userId 사용자 UUID
 * @returns Promise<void>
 */
export async function prefetchChatRooms(
  authToken: string,
  userId: string
): Promise<void> {
  const cacheKey = `prefetch:${userId}`

  // 1. 이미 요청 중이면 재사용 (request deduplication)
  if (pendingRequests.has(cacheKey)) {
    await pendingRequests.get(cacheKey)
    return
  }

  // 2. 캐시에 이미 데이터가 있으면 스킵
  const cached = await chatCache.get(userId)
  if (cached) {
    return
  }

  // 3. 서버에서 데이터 fetch

  const promise = fetch('/api/chat/rooms', {
    headers: { Authorization: `Bearer ${authToken}` }
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const result = await response.json()

      if (result.success && result.data) {
        // 캐시에 저장
        await chatCache.set(userId, result.data)
        return result.data
      } else {
        throw new Error(result.error || 'Failed to prefetch')
      }
    })
    .catch((error) => {
      console.error('❌ [Prefetch] Failed:', error)
      throw error
    })
    .finally(() => {
      // 요청 완료 후 Map에서 제거
      pendingRequests.delete(cacheKey)
    })

  // Map에 등록
  pendingRequests.set(cacheKey, promise)

  await promise
}

/**
 * Hover 이벤트 핸들러용 래퍼
 *
 * @param authToken JWT 토큰
 * @param userId 사용자 UUID
 * @returns Hover event handler
 */
export function createPrefetchHandler(
  authToken: string | null,
  userId: string | null
) {
  return () => {
    if (!authToken || !userId) return

    // 비동기 실행 (hover 블로킹 방지)
    prefetchChatRooms(authToken, userId).catch(console.error)
  }
}
